import React, {
  useState,
  useContext,
  useRef,
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
} from "react";
import { cn } from "../../styles";
import { createPortal } from "react-dom";
import { LocalizationContext, useTranslate } from "../../util/localization";
import { useElementBounds } from "../../helper/useElementBounds";
import { adjustColorForDarkBackground } from "../../helper/color";
import { BaseElementProps, LocalProp, ThemeColor } from "../../props";
import { themeTextStyles } from "../../styles";

const isElementCovered = (
  element: HTMLElement | null,
  backdropElement: HTMLElement
): boolean => {
  if (!element) return true;

  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return true;

  const tolerance = 2;
  const points = [
    { x: rect.left + tolerance, y: rect.top + tolerance }, // top-left
    { x: rect.right - tolerance, y: rect.top + tolerance }, // top-right
    { x: rect.left + tolerance, y: rect.bottom - tolerance }, // bottom-left
    { x: rect.right - tolerance, y: rect.bottom - tolerance }, // bottom-right
  ];

  const portalElements = Array.from(
    document.getElementsByClassName("pss-localization-item")
  ) as HTMLElement[];

  const originalDisplay = backdropElement.style.display;
  const originalPortalDisplays = portalElements.map((el) => el.style.display);

  backdropElement.style.display = "none";
  portalElements.forEach((el) => (el.style.display = "none"));

  const isCovered = points.some((point) => {
    const topElement = document.elementFromPoint(point.x, point.y);
    return (
      topElement !== null &&
      topElement !== element &&
      !element.contains(topElement)
    );
  });

  backdropElement.style.display = originalDisplay;
  portalElements.forEach(
    (el, i) => (el.style.display = originalPortalDisplays[i])
  );

  return isCovered;
};

const formatContent = (content: React.ReactNode) => {
  if (!content) return null;
  if (Array.isArray(content)) content = content.join("");
  if (typeof content !== "string") return content;
  return content.replace(/\\u[\dA-F]{4}/gi, (match) =>
    String.fromCharCode(parseInt(match.replace(/\\u/g, ""), 16))
  );
};

export interface TypographyBaseProps extends Omit<BaseElementProps, "color"> {
  /**
   * Color to be applied to the text.
   * @default ThemeColor.none
   */
  color?: ThemeColor;
  /**
   * Either a string to render or any ReactNode.
   */
  children?: React.ReactNode;

  /**
   * Content to render after the main text. Accepts any React node.
   */
  postfix?: React.ReactNode;

  /**
   * Content to render before the main text. Accepts any React node.
   */
  prefix?: React.ReactNode;

  /**
   * Sets the maximum width of the component. Can be a number (interpreted as pixels) or a string with css units.
   */
  maxWidth?: string | number;

  /**
   * Prevents text wrapping by setting `white-space: nowrap`.
   */
  noWrap?: boolean;

  /**
   * Forces the component to be displayed as a block-level element and enables text wrapping.
   */
  forceWrap?: boolean;

  /**
   * Skips all internal content formatting logic (e.g., Unicode conversion).
   */
  skipFormat?: boolean;

  /**
   * If true, sets the `title` attribute with the full text content for accessibility or tooltips.
   */
  showTitle?: boolean;

  /**
   * Truncates string content to a maximum number of characters and appends an ellipsis (…).
   */
  maxChars?: number;

  /**
   * Renders the typography offscreen. Used to add content for screen readers in the dom that isnt visually rendered.
   */
  visuallyHidden?: boolean;
}

export interface TypographyRenderProps {
  ref: React.Ref<any>;
  title?: string;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export type TypographyRender = (
  props: TypographyRenderProps
) => React.ReactNode;

/**
 * Type for text content that can be either static content as React node or localized.
 * Provides a unified interface for components with nested typography.
 */
export type TypographyContent = React.ReactNode | { local: LocalProp };

export interface TypographyProps extends TypographyBaseProps {
  render?: TypographyRender;
  local?: LocalProp;
  content?: TypographyContent;
}

const Inner = forwardRef<any, TypographyProps>(
  (
    {
      children,
      render,
      className = "",
      style = null,
      color = ThemeColor.none,
      postfix = null,
      prefix = null,
      maxWidth,
      noWrap,
      forceWrap,
      skipFormat,
      showTitle,
      maxChars,
      visuallyHidden = false,
      muted = false,
      ...props
    },
    ref
  ) => {
    let content = children;
    if (!skipFormat) {
      if (typeof content === "string" && maxChars) {
        content = content.substring(0, maxChars) + "\u2026";
      }
      content = formatContent(content);
    }

    const formattedPrefix =
      typeof prefix === "string" && !skipFormat
        ? formatContent(prefix)
        : prefix;
    const formattedPostfix =
      typeof postfix === "string" && !skipFormat
        ? formatContent(postfix)
        : postfix;

    let title: string | undefined = undefined;
    if (showTitle) {
      let titleContent = children;
      if (typeof titleContent !== "string") {
        // Try to join arrays or fallback to empty string
        if (Array.isArray(titleContent)) titleContent = titleContent.join("");
        else titleContent = "";
      }
      let postfixStr =
        typeof postfix === "string"
          ? postfix
          : Array.isArray(postfix)
            ? postfix.join("")
            : "";
      title = String(titleContent) + postfixStr;
    }

    const finalStyle: React.CSSProperties = { ...(style || {}) };
    if (maxWidth) {
      finalStyle.maxWidth =
        typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth;
      finalStyle.overflow = "hidden";
      finalStyle.display = "flow-root";
      finalStyle.whiteSpace = "nowrap";
    }
    if (noWrap) {
      finalStyle.textOverflow = "ellipsis";
      finalStyle.overflow = "hidden";
      finalStyle.whiteSpace = "nowrap";
    } else {
      finalStyle.whiteSpace = "pre-wrap";
    }
    if (forceWrap) {
      finalStyle.display = "block";
    }

    const finalClassName = cn(
      muted && "opacity-50",
      visuallyHidden &&
        "absolute left-[-10000] top-auto w-[1px] h-[1px] overflow-hidden",
      className,
      themeTextStyles[color],
    );

    if (render)
      return render({
        ref,
        children: (
          <>
            {formattedPrefix}
            {content}
            {formattedPostfix}
          </>
        ),
        title,
        className: finalClassName,
        style: finalStyle,
        ...props,
      });

    return (
      <>
        {formattedPrefix}
        {content}
        {formattedPostfix}
      </>
    );
  }
);

export const TypographyBase = forwardRef<any, TypographyProps>(
  ({ local, content, children, ...props }, forwardedRef) => {
    const { edit } = useContext(LocalizationContext) ?? {};
    const [t, { prefixes }] = useTranslate(edit?.state.stringOverrides);
    const anchorRef = useRef(null);
    const [preview, setPreview] = useState<React.ReactNode>(null);
    const [elementRef, bounds, updateBounds] = useElementBounds(
      !!edit,
      edit?.backdropElement
    );
    useImperativeHandle(forwardedRef, () => elementRef.current || null);

    useLayoutEffect(() => {
      if (edit?.state.editingClaim?.anchorEle === anchorRef.current) {
        setPreview(edit?.state.preview);
      } else {
        if (preview) setPreview(null);
        setTimeout(updateBounds);
      }
    }, [
      edit?.state.editingClaim?.anchorEle,
      updateBounds,
      edit?.state.preview,
      edit?.backdropElement,
    ]);

    useLayoutEffect(() => {
      updateBounds();
    }, [preview]);

    if (content && typeof content === "object" && "local" in content)
      local = content.local;
    if (content && !(typeof content === "object" && "local" in content))
      children = content;

    if (!local && !children) return null;

    let localKey = null;
    if (typeof local === "object" && local.key) localKey = local.key;
    if (typeof local === "string") localKey = local;
    if (local === true) localKey = children as string;

    if (!t && local) {
      console.error(
        `Tried to render localized Typography outside of localization context: ${localKey}`
      );
      return <Inner {...props} ref={elementRef}>{`[${localKey}]`}</Inner>;
    }

    if (edit && local && !props.render) {
      console.error(
        `Typography: Localized content "${localKey}" rendered without wrapper element. Localization editing will not work properly. Use a render prop and attch the ref or a wrapper element (eg. Typography.Span)`
      );
    }

    if (!localKey)
      return (
        <Inner {...props} ref={elementRef}>
          {children}
        </Inner>
      );

    const baseOpts = typeof local === "object" ? local.opts : {};
    const localized =
      preview ||
      t(
        localKey,
        typeof local === "object" ? local.context : {},
        edit ? { ...baseOpts, skipPseudoKeys: true } : baseOpts
      );

    return (
      <>
        <Inner {...props} ref={elementRef}>
          {localized}
        </Inner>
        {edit &&
          bounds &&
          edit.backdropElement &&
          createPortal(
            <Inner
              render={({ className, children }) => (
                <span
                  ref={anchorRef}
                  className={className}
                  style={{
                    position: "absolute",
                    ...bounds,
                    ...(elementRef.current
                      ? (() => {
                          try {
                            const styles = window.getComputedStyle(
                              elementRef.current
                            );
                            const originalColor = styles.color;
                            const isVisible =
                              styles.display !== "none" &&
                              styles.visibility !== "hidden" &&
                              styles.opacity !== "0" &&
                              elementRef.current.offsetParent !== null &&
                              !isElementCovered(
                                elementRef.current,
                                edit.backdropElement
                              );

                            return {
                              display: isVisible ? "block" : "none",
                              fontFamily: styles.fontFamily,
                              fontSize: styles.fontSize,
                              fontWeight: styles.fontWeight,
                              lineHeight: styles.lineHeight,
                              color:
                                adjustColorForDarkBackground(originalColor),
                              textAlign: styles.textAlign,
                              textTransform: styles.textTransform,
                              letterSpacing: styles.letterSpacing,
                              textDecoration: styles.textDecoration,
                              fontStyle: styles.fontStyle,
                              backgroundColor: styles.backgroundColor,
                              border: styles.border,
                              borderRadius: styles.borderRadius,
                              boxShadow: styles.boxShadow,
                              padding: styles.padding,
                              margin: 0,
                            };
                          } catch (e) {
                            console.warn("Failed to get computed styles:", e);
                            return {};
                          }
                        })()
                      : {}),
                  }}
                  onClick={(e) => {
                    edit.actions.claimEditing({
                      anchorEle: e.target as HTMLElement,
                      prefixes,
                      local: {
                        key: localKey,
                        context: typeof local === "object" ? local.context : {},
                        opts: typeof local === "object" ? local.opts : {},
                      },
                    });
                    e.stopPropagation();
                  }}
                >
                  {children}
                </span>
              )}
              className="pss-localization-item hover:before:absolute hover:before:inset-[-4px] hover:before:border-2 hover:before:border-primary-500 hover:before:rounded cursor-pointer relative"
            >
              {localized}
            </Inner>,
            edit.backdropElement
          )}
      </>
    );
  }
);
